import { Mic, MicOff, Square, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { VoiceStatus } from "@/lib/voice/conversation";
import { VoiceWaveform } from "./VoiceWaveform";

const ACTIVE_STATUS_COPY: Partial<Record<VoiceStatus, string>> = {
  connecting: "Connecting…",
  listening: "Listening…",
  speaking: "Speaking…",
};

interface VoiceConversationBarProps {
  levels: readonly number[];
  muted: boolean;
  outputMuted: boolean;
  onEnd: () => void;
  onToggleMute: () => void;
  onToggleOutputMute: () => void;
  status: VoiceStatus;
}

/** Active-session controls shared by the full page and compact launcher composer. */
export function VoiceConversationBar({
  levels,
  muted,
  outputMuted,
  onEnd,
  onToggleMute,
  onToggleOutputMute,
  status,
}: VoiceConversationBarProps) {
  let statusCopy = ACTIVE_STATUS_COPY[status] ?? "Voice active";
  if (muted && outputMuted) {
    statusCopy = "Microphone and voice output muted";
  } else if (muted) {
    statusCopy = "Microphone muted";
  } else if (outputMuted) {
    statusCopy = "Voice output muted";
  }

  return (
    <div
      aria-label="Voice conversation controls"
      className="flex min-h-14 items-center gap-2.5 border-b border-border/70 px-2.5 py-2"
      role="group"
    >
      <Button
        aria-label={muted ? "Unmute microphone" : "Mute microphone"}
        aria-pressed={muted}
        className="text-foreground"
        onClick={onToggleMute}
        size="mobileTouchIcon"
        type="button"
        variant="ghost"
      >
        {muted ? (
          <MicOff aria-hidden="true" className="size-4" />
        ) : (
          <Mic aria-hidden="true" className="size-4" />
        )}
      </Button>

      <Button
        aria-label={outputMuted ? "Unmute voice output" : "Mute voice output"}
        aria-pressed={outputMuted}
        className="text-foreground"
        onClick={onToggleOutputMute}
        size="mobileTouchIcon"
        type="button"
        variant="ghost"
      >
        {outputMuted ? (
          <VolumeX aria-hidden="true" className="size-4" />
        ) : (
          <Volume2 aria-hidden="true" className="size-4" />
        )}
      </Button>

      <div className="min-w-0 flex-1">
        <VoiceWaveform levels={levels} muted={muted} />
        <p className="truncate text-center text-muted-foreground text-xs">{statusCopy}</p>
      </div>

      <Button onClick={onEnd} size="mobileTouch" type="button" variant="destructive">
        <Square aria-hidden="true" className="size-3.5 fill-current" />
        End
      </Button>
    </div>
  );
}
